import { Component, OnDestroy, OnInit } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faPowerOff, faSearch } from '@fortawesome/pro-duotone-svg-icons';
import { app } from '@microsoft/teams-js';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, skipWhile, take, takeUntil } from 'rxjs/operators';
import { ThemeType } from 'src/app/samwebDescription/samweb/settings/theme-type';
import { AcdExtensionSearchChanged, SelectAcdExtension } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.actions';
import { SamwinAcdExtensionStateSelectors } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.selectors';
import { TerminateCurrentSession } from 'src/app/store/session/session.actions';
import { SessionState } from 'src/app/store/session/session.state';
import { SetTemporaryTheme } from 'src/app/store/settings/settings.actions';
import { SettingsSelectors } from 'src/app/store/settings/settings.selectors';
import { SettingsState } from 'src/app/store/settings/settings.state';
import { UserAcdExtensionInfoRemoteDto } from 'src/generated/signalR/user-acd-extension-info-remote-dto';

@Component({
   selector: 'samweb--form',
   templateUrl: './acd-selection-form.component.html',
   styleUrls: ['./acd-selection-form.component.scss'],
})
export class AcdSelectionFormComponent implements OnInit, OnDestroy {
   constructor(private store: Store, private translateService: TranslateService) {}

   public searchIcon: IconDefinition = faSearch;

   private terminated$ = new Subject();
   public signOutIcon: IconDefinition = faPowerOff;

   @Select(SamwinAcdExtensionStateSelectors.getFilteredAcdExtensionForSelection)
   public getFilteredAcdExtensionForSelection$: Observable<UserAcdExtensionInfoRemoteDto[]>;

   @Select(SamwinAcdExtensionStateSelectors.getAcdExtensions)
   public allAcdExtensions$: Observable<UserAcdExtensionInfoRemoteDto[]>;

   @Select(SamwinAcdExtensionStateSelectors.getAcdExtensionSearchValue)
   public searchValue$: Observable<string>;

   public translatedWelcomeText$: Observable<string>;
   public logoColor$: Observable<string>;
   public logoPath$: Observable<string>;

   ngOnInit() {
      this.translatedWelcomeText$ = this.store.select(SessionState.getUserFullname).pipe(
         skipWhile((userName) => _(userName).isNil() || _(userName).isEmpty()),
         mergeMap((userName: string) => this.translateService.stream('samwin.acd_selection.welcome_text', { username: userName }))
      );
      app.initialize()
         .then(() => {
            app.getContext().then((context) => {
               const msTeamsTheme = context.app.theme;
               this.store
                  .select(SettingsSelectors.areSettingsInitialized)
                  .pipe(
                     filter((initialized) => initialized),
                     take(1),
                     takeUntil(this.terminated$)
                  )
                  .subscribe(() => {
                     if (msTeamsTheme === 'dark' || msTeamsTheme === 'contrast') {
                        this.store.dispatch(new SetTemporaryTheme(ThemeType.MS_TEAMS_DARK));
                     } else if (msTeamsTheme === 'default') {
                        this.store.dispatch(new SetTemporaryTheme(ThemeType.MS_TEAMS_LIGHT));
                     }
                  });
            });
         })
         .catch(() => {});

      this.logoColor$ = this.store.select(SettingsState.appliedTheme).pipe(
         map((currentTheme) => (currentTheme === ThemeType.MS_TEAMS_DARK || currentTheme === ThemeType.MS_TEAMS_LIGHT ? 'purple' : 'pink')),
         takeUntil(this.terminated$)
      );
      this.logoPath$ = this.logoColor$.pipe(map((logoColor) => `../../../assets/img/sam.hollow.${logoColor}.svg`));
   }

   public selectAcdExtension(selectedAcdExtension: UserAcdExtensionInfoRemoteDto) {
      if (!selectedAcdExtension.isAvailable) {
         return;
      }
      this.store.dispatch(new SelectAcdExtension(selectedAcdExtension));
   }

   ngOnDestroy(): void {
      this.terminated$.next();
      this.terminated$.complete();
   }

   searchTextChanged(value: string) {
      this.store.dispatch(new AcdExtensionSearchChanged(value));
   }

   signOut() {
      this.store.dispatch(new TerminateCurrentSession());
   }
}
